import React from 'react'
import { Tooltip } from '@sketch/components'
import { useHandleCopyValue, CopiableArea } from '@sketch/modules-common'

interface CopiableTextProps extends React.PropsWithChildren<{}> {
  /**
   * The text value to copy to clipboard. If not provided, will use children as string
   */
  copyValue?: string
  /**
   * Tooltip placement
   */
  placement?: 'left' | 'right' | 'top' | 'bottom'
  /**
   * Tooltip spacing
   */
  spacing?: string
  /**
   * Whether to disable the copy functionality
   */
  disabled?: boolean
  /**
   * Custom tooltip text when hovering (before copy)
   */
  hoverTooltip?: string
  /**
   * Custom tooltip text after copying
   */
  copiedTooltip?: string
}

export const CopiableText: React.FCC<CopiableTextProps> = ({
  children,
  copyValue,
  placement = 'left',
  spacing = '10px',
  disabled = false,
  hoverTooltip = 'Copy to clipboard',
}) => {
  const textToCopy = copyValue ?? `${children}`

  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(textToCopy, hoverTooltip)

  if (disabled) {
    return <>{children}</>
  }

  return (
    <CopiableArea
      onClick={handleCopyValue}
      onMouseEnter={handleEnterCopiableArea}
      onMouseLeave={handleLeaveCopiableArea}
    >
      <Tooltip
        placement={placement}
        spacing={spacing}
        visible={copyTooltipVisible}
        content={copyTooltipText}
      >
        {children}
      </Tooltip>
    </CopiableArea>
  )
}
